import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../../components/ui-components/Toast';
import {
  approvalsInbox,
  approvalRequestById,
  approveRequest,
  rejectRequest,
  cancelRequest,
  returnApprovalRequest,
  listDelegations,
  createDelegation,
  disableDelegation,
  listApprovalPolicies,
  getApprovalPolicyById,
  createApprovalPolicy,
  updateApprovalPolicy,
  disableApprovalPolicy,
  searchUsers,
} from '../api';

// --- Query keys ---

export const approvalKeys = {
  // Inbox keys do not encode opts so that all inbox consumers (Sidebar, Inbox page, etc.)
  // share the same cache entry and invalidations refresh them together.
  inbox: () => ['approvals', 'inbox'],
  request: (id) => ['approvalRequest', id],
  delegations: (role) => ['delegations', role ?? 'both'],
  policies: (objectType) => ['approvalPolicies', objectType ?? null],
  policy: (id) => ['approvalPolicy', id],
  users: (q) => ['users', q ?? ''],
};

// --- Inbox ---

/**
 * @param {{ page_size?: number, offset?: number, filter_object_type?: string, filter_requested_by?: string, sort_by?: string, sort_dir?: string }} [opts]
 */
/** Normalize inbox item to snake_case (handles both PascalCase and snake_case from backend) */
function normalizeInboxItem(item) {
  if (!item) return null;
  return {
    approval_request_id: item.approval_request_id ?? item.ApprovalRequestID,
    object_type: item.object_type ?? item.ObjectType ?? '',
    object_id: item.object_id ?? item.ObjectID ?? '',
    transition_type: item.transition_type ?? item.TransitionType ?? '',
    impact_summary: item.impact_summary ?? item.ImpactSummary ?? '',
    impact_amount: item.impact_amount ?? item.ImpactAmount,
    step_index: item.step_index ?? item.StepIndex ?? 0,
    total_steps: item.total_steps ?? item.TotalSteps ?? 0,
    created_at: item.created_at ?? item.CreatedAt ?? '',
    requested_by: item.requested_by ?? item.RequestedBy ?? '',
    inbox_reason: item.inbox_reason ?? item.InboxReason ?? '',
    delegator_user_id: item.delegator_user_id ?? item.DelegatorUserID ?? '',
    // Optional: when backend includes these in inbox response
    budget_name: item.budget_name ?? item.BudgetName ?? '',
    period_label: item.period_label ?? item.PeriodLabel ?? '',
    previous_amount: item.previous_amount ?? item.PreviousAmount,
    requester_role: item.requester_role ?? item.RequesterRole ?? '',
    requester_name: item.requester_name ?? item.RequesterName ?? '',
    governance_trigger_summary: item.governance_trigger_summary ?? item.GovernanceTriggerSummary ?? '',
  };
}

export function useApprovalsInboxQuery(opts = {}) {
  return useQuery({
    queryKey: approvalKeys.inbox(opts),
    queryFn: async () => {
      const res = await approvalsInbox(opts);
      const rawList = res?.inbox ?? res ?? [];
      return Array.isArray(rawList) ? rawList.map(normalizeInboxItem).filter(Boolean) : [];
    },
    refetchOnWindowFocus: true,
  });
}

// --- Approval Request ---

/** Normalize approval request to snake_case (handles both PascalCase and snake_case from backend) */
function normalizeApprovalRequest(raw) {
  if (!raw) return null;
  const r = raw;
  const steps = (r.steps ?? r.Steps ?? []).map((s) => ({
    id: s.id,
    step_index: s.step_index ?? s.StepIndex,
    label: s.label ?? s.Label ?? null,
    role: s.role ?? s.Role ?? null,
    approver_role: s.approver_role ?? s.ApproverRole ?? null,
    status: s.status ?? s.Status,
    approver_user_id: s.approver_user_id ?? s.ApproverUserID,
    decided_at: s.decided_at ?? s.DecidedAt,
    decided_by: s.decided_by ?? s.DecidedBy,
    comment: s.comment ?? s.Comment,
    acted_as_delegate: s.acted_as_delegate ?? s.ActedAsDelegate ?? false,
    delegator_user_id: s.delegator_user_id ?? s.DelegatorUserID ?? '',
  }));
  return {
    id: r.id,
    status: r.status ?? r.Status,
    object_type: r.object_type ?? r.ObjectType ?? '',
    object_id: r.object_id ?? r.ObjectID ?? '',
    transition_type: r.transition_type ?? r.TransitionType ?? '',
    impact_summary: r.impact_summary ?? r.ImpactSummary ?? '',
    impact_amount: r.impact_amount ?? r.ImpactAmount,
    impact_currency: r.impact_currency ?? r.ImpactCurrency ?? '',
    reason: r.reason ?? r.Reason ?? '',
    policy_snapshot_json: r.policy_snapshot_json ?? r.PolicySnapshotJSON ?? {},
    created_at: r.created_at ?? r.CreatedAt ?? '',
    created_by: r.created_by ?? r.CreatedBy ?? '',
    steps,
  };
}

/** @param {string|number|null} id */
export function useApprovalRequestQuery(id) {
  return useQuery({
    queryKey: approvalKeys.request(id),
    queryFn: async () => {
      const res = await approvalRequestById(id);
      const raw = res?.request ?? res;
      const normalized = normalizeApprovalRequest(raw);
      if (normalized && res?.users) {
        normalized.users = res.users;
      }
      if (normalized && res?.object_context) {
        normalized.object_context = res.object_context;
      }
      return normalized;
    },
    enabled: !!id,
  });
}

// --- Approve / Reject / Return / Cancel mutations ---

// Maps the hard-SoD / authority-gate denial codes shared by approve, reject and return
// (see backend_go internal/approvals/errors.go + service.go checkHardSoD/checkCanApprove)
// to messages an approver can act on. Falls back to the server's own message.
export function friendlyApprovalDenialMessage(err, action = 'process') {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  switch (code) {
    case 'SELF_APPROVAL':
      return "You can't approve a request you prepared";
    case 'POLICY_AUTHOR':
      return "You authored this approval policy — you can't approve under it";
    case 'AMOUNT_EXCEEDS_AUTHORITY':
      return 'This request exceeds your approval limit';
    case 'OUT_OF_AUTHORITY_SCOPE':
      return 'This request is outside your approval scope';
    case 'AUTHORITY_EXPIRED':
      return 'Your approval authority has expired';
    case 'NO_AUTHORITY':
      return "You don't have approval authority for this";
    case 'NO_ELIGIBLE_APPROVER':
      return 'No eligible approver has authority for this request';
    default:
      return serverMessage || err?.message || `Failed to ${action}`;
  }
}

export function useApproveApprovalRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, comment }) => {
      const res = await approveRequest(id, comment ? { comment } : {});
      return res?.request ?? res;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(approvalKeys.request(id));
      queryClient.invalidateQueries(approvalKeys.inbox());
      // Cockpit shows period versions; when approval is approved, period gets activated
      queryClient.invalidateQueries({ queryKey: ['periodVersions'] });
      queryClient.invalidateQueries({ queryKey: ['budgetPeriods'] });
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: (err) => {
      toast.error(friendlyApprovalDenialMessage(err, 'approve'));
    },
  });
}

export function useRejectApprovalRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, comment }) => {
      const res = await rejectRequest(id, { comment });
      return res?.request ?? res;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(approvalKeys.request(id));
      queryClient.invalidateQueries(approvalKeys.inbox());
      queryClient.invalidateQueries({ queryKey: ['periodVersions'] });
      queryClient.invalidateQueries({ queryKey: ['budgetPeriods'] });
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: (err) => {
      toast.error(friendlyApprovalDenialMessage(err, 'reject'));
    },
  });
}

export function useReturnApprovalRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, comment }) => {
      const res = await returnApprovalRequest(id, { comment });
      return res?.request ?? res;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(approvalKeys.request(id));
      queryClient.invalidateQueries(approvalKeys.inbox());
      queryClient.invalidateQueries({ queryKey: ['periodVersions'] });
      queryClient.invalidateQueries({ queryKey: ['budgetPeriods'] });
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: (err) => {
      toast.error(friendlyApprovalDenialMessage(err, 'return'));
    },
  });
}

export function useCancelApprovalRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await cancelRequest(id);
      return res?.request ?? res;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(approvalKeys.request(id));
      queryClient.invalidateQueries(approvalKeys.inbox());
      queryClient.invalidateQueries({ queryKey: ['periodVersions'] });
      queryClient.invalidateQueries({ queryKey: ['budgetPeriods'] });
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.message || 'Failed to cancel';
      toast.error(msg);
    },
  });
}

// --- Delegations ---

/** @param {'delegator'|'delegate'|'both'} [role] */
export function useDelegationsQuery(role = 'both') {
  return useQuery({
    queryKey: approvalKeys.delegations(role),
    queryFn: async () => {
      const res = await listDelegations({ role });
      return res?.delegations ?? [];
    },
  });
}

export function useCreateDelegationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createDelegation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(approvalKeys.delegations());
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.serverBody?.message || err?.message || 'Failed to create delegation';
      toast.error(msg);
    },
  });
}

export function useDisableDelegationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => disableDelegation(id),
    onSuccess: () => {
      queryClient.invalidateQueries(approvalKeys.delegations());
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.serverBody?.message || err?.message || 'Failed to disable delegation';
      toast.error(msg);
    },
  });
}

// --- Users ---

/** @param {string} search - min 2 chars to trigger */
export function useUsersSearchQuery(search) {
  return useQuery({
    queryKey: approvalKeys.users(search),
    queryFn: async () => {
      const res = await searchUsers(search);
      return res?.users ?? [];
    },
    enabled: typeof search === 'string' && search.length >= 2,
    staleTime: 30_000,
  });
}

// --- Approval Policies ---

function normalizeApprovalPolicy(item) {
  if (!item) return null;
  return {
    id: item.id ?? item.ID,
    customer_id: item.customer_id ?? item.CustomerID ?? '',
    object_type: item.object_type ?? item.ObjectType ?? '',
    name: item.name ?? item.Name ?? '',
    is_enabled: item.is_enabled ?? item.IsEnabled ?? true,
    policy_json: item.policy_json ?? item.PolicyJSON ?? {},
    created_at: item.created_at ?? item.CreatedAt ?? '',
    created_by: item.created_by ?? item.CreatedBy ?? '',
    updated_at: item.updated_at ?? item.UpdatedAt ?? '',
    updated_by: item.updated_by ?? item.UpdatedBy ?? '',
  };
}

/** @param {string|number|null} id */
export function useApprovalPolicyQuery(id) {
  return useQuery({
    queryKey: approvalKeys.policy(id),
    queryFn: async () => {
      const res = await getApprovalPolicyById(id);
      const raw = res?.policy ?? res;
      return normalizeApprovalPolicy(raw);
    },
    enabled: !!id,
  });
}

/** @param {string|null} [objectType] */
export function useApprovalPoliciesQuery(objectType = null) {
  return useQuery({
    queryKey: approvalKeys.policies(objectType),
    queryFn: async () => {
      const res = await listApprovalPolicies(objectType ? { object_type: objectType } : {});
      const rawList = res?.policies ?? [];
      return Array.isArray(rawList) ? rawList.map(normalizeApprovalPolicy).filter(Boolean) : [];
    },
  });
}

export function useCreateApprovalPolicyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createApprovalPolicy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(approvalKeys.policies());
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.serverBody?.message || err?.message || 'Failed to create policy';
      toast.error(msg);
    },
  });
}

export function useUpdateApprovalPolicyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateApprovalPolicy(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(approvalKeys.policy(id));
      queryClient.invalidateQueries(approvalKeys.policies());
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.serverBody?.message || err?.message || 'Failed to update policy';
      toast.error(msg);
    },
  });
}

export function useDisableApprovalPolicyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => disableApprovalPolicy(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(approvalKeys.policy(id));
      queryClient.invalidateQueries(approvalKeys.policies());
    },
    onError: (err) => {
      const msg = err?.serverBody?.error?.message || err?.serverBody?.message || err?.message || 'Failed to disable policy';
      toast.error(msg);
    },
  });
}
